import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Send, Receipt, PiggyBank, Banknote, Users, 
  CreditCard, TrendingUp, History, User, LogOut,
  Menu, X, Bell, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mockUser } from '@/utils/mockData';

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Send className="w-5 h-5" />, label: 'Transfer', path: '/dashboard/transfer' },
    { icon: <Receipt className="w-5 h-5" />, label: 'Bills', path: '/dashboard/bills' },
    { icon: <PiggyBank className="w-5 h-5" />, label: 'Savings', path: '/dashboard/savings' },
    { icon: <Banknote className="w-5 h-5" />, label: 'Loans', path: '/dashboard/loans' },
    { icon: <Users className="w-5 h-5" />, label: 'Cooperatives', path: '/dashboard/cooperatives' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Cards', path: '/dashboard/cards' },
    { icon: <TrendingUp className="w-5 h-5" />, label: 'Budget', path: '/dashboard/budget' },
    { icon: <History className="w-5 h-5" />, label: 'Transactions', path: '/dashboard/transactions' },
    { icon: <User className="w-5 h-5" />, label: 'Profile', path: '/dashboard/profile' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('britedge_auth');
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 w-full bg-white border-b border-slate-200 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img 
            src="https://britedgeng.com/images/logo.png" 
            alt="Britedge" 
            className="h-8"
          />
          <button data-testid="mobile-notifications-btn">
            <Bell className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-40 transition-transform duration-300
        lg:translate-x-0 lg:w-72 w-72
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <img 
              src="https://britedgeng.com/images/logo.png" 
              alt="Britedge" 
              className="h-10 w-auto"
            />
          </div>

          {/* User Info */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold text-lg">
                {mockUser.firstName.charAt(0)}{mockUser.lastName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{mockUser.firstName} {mockUser.lastName}</p>
                <p className="text-sm text-slate-600 truncate">{mockUser.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                data-testid={`nav-${item.label.toLowerCase()}-btn`}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${isActive(item.path)
                    ? 'bg-brand-blue text-white shadow-lg shadow-[#221a83]/20'
                    : 'text-slate-700 hover:bg-slate-100'
                  }
                `}
              >
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-200">
            <button
              onClick={handleLogout}
              data-testid="nav-logout-btn"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
